import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { toast } from 'sonner';

export type DowntimeCategory =
  | 'failure'
  | 'maintenance'
  | 'inspection'
  | 'calibration'
  | 'external'
  | 'other';

export interface AssetDowntimeEvent {
  id: string;
  asset_id: string;
  shop_id: string;
  started_at: string;
  ended_at: string | null;
  reason: string;
  category: DowntimeCategory;
  notes: string | null;
  previous_status: string | null;
  created_by: string | null;
  closed_by: string | null;
  created_at: string;
  duration_minutes: number | null;
}

export const DOWNTIME_CATEGORY_LABELS: Record<DowntimeCategory, string> = {
  failure: 'Failure',
  maintenance: 'Maintenance',
  inspection: 'Inspection',
  calibration: 'Calibration',
  external: 'External',
  other: 'Other',
};

/**
 * Fetch all downtime events for a single asset (most recent first).
 */
export function useAssetDowntime(assetId: string | null) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['asset-downtime', assetId],
    queryFn: async () => {
      if (!assetId || !shopId) return [] as AssetDowntimeEvent[];

      const { data, error } = await supabase
        .from('shop_asset_downtime_events')
        .select('*')
        .eq('asset_id', assetId)
        .eq('shop_id', shopId)
        .order('started_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as AssetDowntimeEvent[];
    },
    enabled: !!assetId && !!shopId,
  });
}

/**
 * Convenience: returns the currently active downtime event for an asset, if any.
 */
export function useActiveAssetDowntime(assetId: string | null) {
  const { data, isLoading } = useAssetDowntime(assetId);
  const active = data?.find((e) => e.ended_at === null) ?? null;
  return { active, isLoading };
}

export function useStartAssetDowntime() {
  const { activeShop, user } = useAuth();
  const { canEditAssets } = usePermissions();
  const queryClient = useQueryClient();
  const shopId = activeShop?.id;

  return useMutation({
    mutationFn: async (input: {
      asset_id: string;
      reason: string;
      category: DowntimeCategory;
      notes?: string;
    }) => {
      if (!shopId) throw new Error('No active shop');
      if (!canEditAssets) throw new Error('You do not have permission to change asset status');

      const { data, error } = await supabase
        .from('shop_asset_downtime_events')
        .insert({
          shop_id: shopId,
          asset_id: input.asset_id,
          reason: input.reason,
          category: input.category,
          notes: input.notes ?? null,
          created_by: user?.id ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      return data as AssetDowntimeEvent;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['asset-downtime', data.asset_id] });
      queryClient.invalidateQueries({ queryKey: ['shop-asset', data.asset_id] });
      queryClient.invalidateQueries({ queryKey: ['shop-assets'] });
      queryClient.invalidateQueries({ queryKey: ['shop-assets-summary'] });
      toast.success('Asset marked out of service');
    },
    onError: (error: Error) => {
      toast.error('Failed to start downtime', { description: error.message });
    },
  });
}

export function useEndAssetDowntime() {
  const { user } = useAuth();
  const { canEditAssets } = usePermissions();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId: string) => {
      if (!canEditAssets) throw new Error('You do not have permission to change asset status');

      const { data, error } = await supabase
        .from('shop_asset_downtime_events')
        .update({
          ended_at: new Date().toISOString(),
          closed_by: user?.id ?? null,
        })
        .eq('id', eventId)
        .select()
        .single();

      if (error) throw error;
      return data as AssetDowntimeEvent;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['asset-downtime', data.asset_id] });
      queryClient.invalidateQueries({ queryKey: ['shop-asset', data.asset_id] });
      queryClient.invalidateQueries({ queryKey: ['shop-assets'] });
      queryClient.invalidateQueries({ queryKey: ['shop-assets-summary'] });
      toast.success('Asset returned to service');
    },
    onError: (error: Error) => {
      toast.error('Failed to close downtime', { description: error.message });
    },
  });
}

/**
 * Shop-level analytics summary RPC for the OptiAssets dashboard.
 */
export interface ShopAssetsSummary {
  total_assets: number;
  assets_down: number;
  maintenance_due_30d: number;
  downtime_hours_mtd: number;
  maintenance_spend_mtd: number;
}

export function useShopAssetsSummary() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['shop-assets-summary', shopId],
    queryFn: async () => {
      if (!shopId) return null;
      const { data, error } = await supabase.rpc('get_shop_assets_summary', {
        p_shop_id: shopId,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row ?? null) as ShopAssetsSummary | null;
    },
    enabled: !!shopId,
  });
}
